import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getArticles } from '../core/api/content-service';
import { formatPlainDate } from '../core/dates/plain-date';
import { describeApiError } from '../core/errors/describe-api-error';
import type { TranslationKey } from '../core/i18n/translation';
import { useLanguage } from '../core/hooks/use-language';
import { usePageMetadata } from '../core/hooks/use-page-metadata';
import { STATIC_PAGES, staticPageMetadata } from '../core/seo/static-pages';
import { useTranslation } from '../core/hooks/use-translation';
import type { PublicArticleListItem } from '../core/models/public-article-list-item';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { EmptyState } from '../ui/empty-state';
import { Skeleton } from '../ui/skeleton';
import { ArrowLeftIcon, ArrowRightIcon, DocumentIcon } from '../ui/portal-icons';

/**
 * The blog index in one language.
 *
 * **It lists this language and no other.** An article published in Kazakh and not yet translated
 * is absent here on `/ru/blog` rather than present and empty — the rule the per-language
 * publication state exists to make expressible, and the one a single `IsPublished` boolean would
 * have forced a choice about.
 *
 * The page number lives in the query string, so the back button works and a link to page 2 is a
 * link to page 2.
 *
 * **The heading and the first card shape are in the served bytes; the articles are not.** This page
 * is written to a file at build time like the other content pages, but a build cannot know what has
 * been published since — so what the file carries is the frame, and the list arrives from the API a
 * moment later into the card outlines this draws while it waits. An individual article's page is
 * the case that *is* fully written, by the API, when it is published.
 */
export function BlogPage() {
    const { t } = useTranslation();
    const language = useLanguage();
    const [searchParams, setSearchParams] = useSearchParams();

    const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

    const [articles, setArticles] = useState<PublicArticleListItem[]>([]);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<TranslationKey | null>(null);

    usePageMetadata(staticPageMetadata(STATIC_PAGES[3], language));

    useEffect(() => {
        const controller = new AbortController();

        setLoading(true);
        setError(null);

        getArticles(language, page, controller.signal)
            .then((result) => {
                setArticles(result.items);
                setTotalPages(result.totalPages);
                setLoading(false);
            })
            .catch((cause: unknown) => {
                if (controller.signal.aborted) {
                    return;
                }

                setError(describeApiError(cause));
                setLoading(false);
            });

        return () => controller.abort();
    }, [language, page]);

    return (
        <div className="mx-auto max-w-5xl px-4 py-12 sm:py-16">
            <header className="max-w-2xl">
                <h1 className="text-3xl font-extrabold text-slate-900 sm:text-4xl dark:text-slate-100">
                    {t('blog.title')}
                </h1>
                <p className="mt-3 text-lg text-slate-600 dark:text-slate-400">{t('blog.subtitle')}</p>
            </header>

            {error !== null && (
                <div className="mt-10">
                    <Alert>{t(error)}</Alert>
                </div>
            )}

            {loading && (
                <div role="status" aria-live="polite" className="mt-10 grid gap-6 sm:grid-cols-2">
                    <span className="sr-only">{t('common.loading')}</span>
                    <Skeleton className="h-72 rounded-2xl" />
                    <Skeleton className="h-72 rounded-2xl" />
                </div>
            )}

            {!loading && error === null && articles.length === 0 && (
                <div className="mt-10">
                    <EmptyState icon={<DocumentIcon />} title={t('blog.empty')} />
                </div>
            )}

            {articles.length > 0 && (
                <div className="mt-10 grid gap-6 sm:grid-cols-2">
                    {articles.map((article) => (
                        <article
                            key={article.slug}
                            className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-900/5 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-brand-700"
                        >
                            {article.coverImageUrl !== null && (
                                <img
                                    src={article.coverImageUrl}
                                    alt={article.coverImageAlt ?? ''}
                                    className="h-44 w-full object-cover"
                                    loading="lazy"
                                />
                            )}

                            <div className="flex flex-1 flex-col p-5">
                                <time
                                    className="text-xs font-semibold tracking-wide text-slate-400 uppercase dark:text-slate-500"
                                    dateTime={article.publishedAt}
                                >
                                    {formatPlainDate(article.publishedAt.slice(0, 10), language, {
                                        day: '2-digit',
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                </time>

                                <h2 className="mt-2 text-lg font-bold text-slate-900 dark:text-slate-100">
                                    {/*
                                        The heading holds the only link, and the whole card lifts with
                                        it: a second "read more" anchor to the same URL is one more
                                        stop for a keyboard and one more identical line for a screen
                                        reader, on a card whose title already says where it goes.
                                    */}
                                    <Link
                                        to={`/${language}/blog/${article.slug}`}
                                        className="after:absolute after:inset-0 group-hover:text-brand-700 dark:group-hover:text-brand-300"
                                    >
                                        {article.title}
                                    </Link>
                                </h2>

                                {article.excerpt !== null && (
                                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                                        {article.excerpt}
                                    </p>
                                )}

                                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 dark:text-brand-400">
                                    {t('blog.read_more')}
                                    <ArrowRightIcon className="size-4 transition group-hover:translate-x-0.5" />
                                </span>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <nav className="mt-12 flex items-center justify-between gap-4">
                    <Button
                        variant="secondary"
                        disabled={page <= 1}
                        onClick={() => setSearchParams({ page: String(page - 1) })}
                    >
                        <ArrowLeftIcon className="size-4" />
                        {t('blog.previous')}
                    </Button>

                    <span className="text-sm text-slate-500 tabular-nums dark:text-slate-400">
                        {`${page} / ${totalPages}`}
                    </span>

                    <Button
                        variant="secondary"
                        disabled={page >= totalPages}
                        onClick={() => setSearchParams({ page: String(page + 1) })}
                    >
                        {t('blog.next')}
                        <ArrowRightIcon className="size-4" />
                    </Button>
                </nav>
            )}
        </div>
    );
}
